import { exercise, section, choice, source, equation, table, example } from '../batch2/shared.ts';
export const batch4Foundations = [
  exercise({
    id: 'foundations-membranes',
    category: 'foundations',
    title: 'Membranes and movement',
    topic: 'Diffusion, selective permeability and osmosis',
    difficulty: 'Challenging',
    reading: {
      title: 'Explain movement before naming the solution',
      sources: [
        source(
          'OpenStax Biology 2e · Passive Transport',
          'https://openstax.org/books/biology-2e/pages/5-2-passive-transport',
        ),
        source(
          'OpenStax Anatomy and Physiology 2e · The Cell Membrane',
          'https://openstax.org/books/anatomy-and-physiology-2e/pages/3-1-the-cell-membrane',
        ),
      ],
      sections: [
        section('diffusion', 'Random motion can produce a directed net flow', [
          'Dissolved particles move because of thermal motion. They do not need to detect where the concentration is lower and decide to travel there. If one region contains many more particles than another, more particles are available to cross from the crowded region in a given interval. Crossings occur in both directions, but their difference can give a net movement from higher concentration toward lower concentration. This spreading is diffusion.',
          'Concentration means an amount per volume. Twice as many particles in twice the volume gives the same concentration. A concentration gradient is a spatial difference in concentration. Its existence can support a net flow, but only if a physical path permits the substance to move. A membrane can maintain a concentration difference for a substance that cannot cross it.',
          example(
            'Count crossings rather than intentions',
            'Imagine equal-sized neighboring compartments containing 100 and 20 freely moving particles. If each particle has the same small chance of crossing in a short interval, the fuller compartment supplies more crossings on average. Once the concentrations become equal, particles still cross in both directions; equal opposing flows give zero net transfer.',
          ),
          'Equilibrium therefore does not mean that molecular motion has stopped. Nor does passive transport mean that the particles have no energy. It means that the cell is not directly paying metabolic energy to drive the transport against its relevant gradient. For charged particles, electrical forces as well as concentration matter; this unit’s simple concentration examples use uncharged solutes or explicitly exclude electrical effects.',
        ]),
        section('selectivity', 'A membrane is a selective boundary', [
          'A cell membrane’s lipid interior is not equally easy for every substance to cross. Small nonpolar molecules such as oxygen can pass through the lipid bilayer relatively readily. Ions and many polar molecules require particular proteins or other transport processes. Selectively permeable means that some substances can cross much more easily than others, not that the membrane is either completely open or completely sealed.',
          'Facilitated diffusion uses a channel or carrier protein while still allowing movement down the relevant gradient. The word facilitated describes assistance crossing the membrane, not necessarily an energy payment. Active transport, by contrast, uses an energy source to move a substance against its gradient or couples its movement to another energy-releasing process.',
          table(
            'Separate the path from the energy source',
            ['Process', 'Path or mechanism', 'Direction in a simple concentration model'],
            [
              [
                'Simple diffusion',
                'Through a permeable medium or bilayer',
                'Down the concentration gradient',
              ],
              [
                'Facilitated diffusion',
                'Through a suitable membrane protein',
                'Down the relevant gradient',
              ],
              [
                'Active transport',
                'Energy-coupled transport machinery',
                'Can move against the relevant gradient',
              ],
            ],
          ),
          'A protein’s presence alone does not classify transport as active. Ask whether the process is driven down an existing gradient or requires energy coupling to move against it. A cell can also spend energy building a gradient and later use that stored gradient to drive another process. The complete energy history and the immediate transport step are related but distinct questions.',
        ]),
        section('osmosis', 'Follow the water that can cross', [
          'Osmosis is net water movement across a selectively permeable membrane. In a simple setup with equal initial pressure and a dissolved solute that cannot cross, water moves toward the side with the higher solute-particle concentration. This tendency is a consequence of water’s chemical potential: dissolved particles change the free-energy balance for moving water. The rule is not that water is consciously trying to dilute something.',
          'Water movement changes the volumes and therefore the concentrations. It can also create a pressure difference. An opposing pressure can halt net osmosis before solute concentrations become equal, so equal concentrations are not the only possible endpoint. Always identify what can cross and whether pressure matters before using a concentration comparison.',
          example(
            'A membrane between two chambers',
            'Suppose equal-pressure chambers contain the same nonpenetrating solute at concentrations of 0.1 and 0.3 mol/L, and only water can cross. Initially water moves net toward the 0.3 mol/L side. The solute particles themselves do not cross. If that side builds sufficient opposing pressure, net flow can stop without the two solute concentrations becoming identical.',
          ),
          'Particle concentration matters, not merely grams of dissolved material. Different substances can have different masses per particle, and some dissociate into multiple ions. A mass comparison alone therefore cannot settle an osmotic comparison. In the questions, solute behavior is specified so the intended particle comparison is unambiguous.',
        ]),
        section('cells', 'Use tonicity to predict a cell’s volume change', [
          'Tonicity describes the effect an external solution has on cell volume and depends on solutes that do not freely penetrate the membrane on the relevant timescale. Relative to the cell, a hypertonic outside solution draws water out and tends to shrink the cell. A hypotonic outside solution allows net water entry and tends to swell it. An isotonic solution produces no sustained net volume change under the stated conditions.',
          table(
            'Nonpenetrating-solute comparison',
            [
              'Outside relative to inside',
              'Initial net water movement',
              'Typical animal-cell response',
            ],
            [
              ['Higher concentration', 'Outward', 'Shrinks'],
              ['Lower concentration', 'Inward', 'Swells'],
              ['Equal concentration at equal pressure', 'No net movement', 'Volume remains steady'],
            ],
          ),
          'An animal cell lacks a rigid cell wall, so excessive water entry can rupture it. A plant cell has a wall that resists expansion; pressure builds as water enters, producing turgor. The wall does not stop water molecules from crossing the membrane by becoming an impermeable seal. It supplies mechanical resistance to continued expansion.',
          'Two solutions with the same total particle concentration are not necessarily equally tonic if one solute can enter the cell and the other cannot. This is why “count all particles” is a useful osmolarity calculation but not a universal prediction of lasting volume change. For the daily simplified examples, the prompt explicitly makes the solute nonpenetrating and starts with equal pressure. Those conditions license the direct comparison rather than hiding a biological exception.',
        ]),
      ],
    },
    questions: [
      choice(
        'b4-membrane-1',
        'A permeable boundary separates equal volumes with unequal concentrations of an uncharged solute. What explains the initial net diffusion down the gradient?',
        [
          'Particles in the dilute side stop moving.',
          'Only particles on the concentrated side can cross.',
          'Random crossings occur both ways, with more leaving the more concentrated side.',
          'Particles acquire a preference for the less crowded destination.',
        ],
        2,
        'Both directions occur; unequal numbers available produce unequal average crossing rates.',
        ['diffusion'],
      ),
      choice(
        'b4-membrane-2',
        'At diffusion equilibrium, which statement is correct?',
        [
          'Particles still move, but the opposing average flows balance.',
          'Every particle remains on its original side.',
          'All particle kinetic energy has been used up.',
          'The membrane must have become impermeable.',
        ],
        0,
        'Zero net flow does not mean zero microscopic motion.',
        ['diffusion'],
      ),
      choice(
        'b4-membrane-3',
        'Glucose moves through a carrier down its concentration gradient, without direct metabolic energy input. What is this?',
        [
          'Active transport because a protein is involved',
          'Osmosis because the path crosses a membrane',
          'Simple diffusion through the lipid interior',
          'Facilitated diffusion',
        ],
        3,
        'Protein assistance can support passive, downhill transport.',
        ['diffusion', 'selectivity'],
      ),
      choice(
        'b4-membrane-4',
        'Which observation is strongest evidence that a transport process is energy-coupled rather than simple passive diffusion?',
        [
          'The transported molecule is small.',
          'An uncharged solute moves steadily from lower to higher concentration with no other driving gradient.',
          'A membrane protein is present.',
          'Particles move in both directions across a boundary.',
        ],
        1,
        'Sustained uphill movement in the specified model requires an energy source.',
        ['diffusion', 'selectivity'],
      ),
      choice(
        'b4-membrane-5',
        'Equal-pressure chambers contain a nonpenetrating solute at 0.2 and 0.5 mol/L. Only water can cross the membrane. What is the initial net movement?',
        [
          'Solute moves from 0.5 toward 0.2 mol/L.',
          'Water moves toward the 0.5 mol/L side.',
          'Water moves toward the 0.2 mol/L side.',
          'No movement is possible because the solute cannot cross.',
        ],
        1,
        'Water can move even when the solute cannot; it initially moves toward the more concentrated solute side.',
        ['osmosis'],
      ),
      choice(
        'b4-membrane-6',
        'An animal cell is placed in a solution with a higher concentration of the same nonpenetrating solute than its interior, initially at equal pressure. What is expected?',
        [
          'Solute leaves and the cell swells.',
          'Water enters and the cell swells.',
          'The solute difference prevents water exchange.',
          'Water leaves and the cell shrinks.',
        ],
        3,
        'The outside is hypertonic in this model, producing net water loss.',
        ['osmosis', 'cells'],
      ),
      choice(
        'b4-membrane-7',
        'Why can net osmosis stop while the two sides still have different solute concentrations?',
        [
          'An opposing pressure difference can balance the osmotic tendency.',
          'The water molecules have ceased all thermal motion.',
          'The solute must have crossed despite being nonpenetrating.',
          'The membrane must have disappeared.',
        ],
        0,
        'Pressure can balance the chemical-potential effect of the concentration difference.',
        ['osmosis'],
      ),
      choice(
        'b4-membrane-8',
        'Two solutions have equal total particle concentrations, but one solute can freely enter a cell and the other cannot. What follows?',
        [
          'They must cause identical long-term cell-volume changes.',
          'Both must be hypertonic regardless of the cell interior.',
          'Equal particle counts alone do not establish equal tonicity.',
          'The permeating solute cannot affect any concentration.',
        ],
        2,
        'Tonicity depends on effective nonpenetrating solutes and timescale, not just total osmolarity.',
        ['osmosis', 'cells'],
      ),
      choice(
        'b4-membrane-9',
        'Why does a plant cell often resist bursting in a hypotonic environment better than an animal cell?',
        [
          'Its wall permanently blocks all water molecules.',
          'Its cell wall generates mechanical resistance as internal pressure rises.',
          'Its membrane turns water into solute.',
          'Its cytoplasm has no dissolved particles.',
        ],
        1,
        'The wall resists expansion and supports turgor; it is not an impermeable water seal.',
        ['osmosis', 'cells'],
      ),
      choice(
        'b4-membrane-10',
        'A solute doubles in amount while its solution volume also doubles. What happens to its concentration?',
        [
          'It stays the same.',
          'It doubles.',
          'It halves.',
          'It becomes zero because the volumes changed.',
        ],
        0,
        'Concentration is amount divided by volume; doubling both preserves the ratio.',
        ['diffusion'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-circuits',
    category: 'foundations',
    title: 'Simple electric circuits',
    topic: 'Current, voltage, resistance and series/parallel paths',
    difficulty: 'Challenging',
    reading: {
      title: 'Follow charge and account for energy',
      sources: [
        source(
          'OpenStax College Physics · Resistors in Series and Parallel',
          'https://openstax.org/books/college-physics/pages/21-1-resistors-in-series-and-parallel',
        ),
      ],
      sections: [
        section('quantities', 'Three quantities answer different questions', [
          'Electric current is a rate of charge flow. One ampere means one coulomb of charge passes a cross-section each second. Voltage is electric potential-energy difference per unit charge: one volt corresponds to one joule per coulomb. A battery can maintain a potential difference between its terminals; a complete conducting circuit lets charge circulate while energy is transferred to components.',
          'Resistance describes how much voltage is needed to sustain a particular current through a component. For an ohmic resistor under fixed physical conditions, current is proportional to voltage and the ratio V/I is constant. This empirical model is summarized as V = IR. It is not a universal claim about every electrical device under every temperature or operating condition.',
          equation(
            'Ohmic resistor',
            'V',
            'I × R',
            'V is voltage across the resistor in volts, I is current through it in amperes, and R is resistance in ohms.',
          ),
          example(
            'A single resistor',
            'A 4-ohm resistor connected across an ideal 12-volt source carries 12/4 = 3 amperes. In one second, three coulombs pass through it. The resistor transfers electrical energy to its surroundings, but does not consume those three coulombs and make them disappear.',
          ),
          'Current and energy transfer are often confused. In a steady single loop, the same rate of charge passes before and after a resistor. The charges’ electrical potential energy changes, while charge is conserved. An ideal voltage source supplies energy per charge; it does not force the same current through every possible external resistance.',
        ]),
        section('series', 'One path means the same current', [
          'Two resistors are in series when there is no branch between them: the current through one must continue through the other. In steady operation, charge does not accumulate indefinitely at their connection, so their currents are equal. The energy per charge lost across the whole pair is the sum lost across each component. Their voltage drops therefore add.',
          'Substitute Ohm’s law into that energy statement. If the common current is I, then the total voltage is IR₁ + IR₂ = I(R₁ + R₂). A single equivalent resistor carrying the same current at the same total voltage must therefore have resistance R₁ + R₂. The formula follows from the shared current and summed voltage, not from the physical closeness of the parts.',
          example(
            'A series divider',
            'Connect 2-ohm and 4-ohm resistors in series across an ideal 12-volt source. Their total resistance is 6 ohms and their common current is 2 amperes. The voltage drops are 4 volts and 8 volts. They add to 12; they are not equal because the resistances differ.',
            table(
              'Series accounting',
              ['Component', 'Current', 'Voltage drop'],
              [
                ['2 Ω', '2 A', '4 V'],
                ['4 Ω', '2 A', '8 V'],
                ['Whole pair', '2 A', '12 V'],
              ],
            ),
          ),
          'Adding a positive resistance in series to a fixed ideal voltage source increases the total resistance and reduces the current. Opening the only path stops steady current through every series component. These conclusions depend on the stated circuit connections, not on whether the drawing places resistors in a horizontal row.',
        ]),
        section('parallel', 'Shared endpoints mean the same voltage', [
          'Resistors are in parallel when both connect between the same two nodes. Ideal wires have negligible resistance, so each node is at one potential and every branch sees the same potential difference between those nodes. The branch currents can differ: by Ohm’s law, the branch with lower resistance carries more current at that shared voltage.',
          {
            kind: 'diagram',
            drawing: 'circuit-paths',
            caption: 'Two equal resistors behave differently when their connections change.',
            description:
              'Two six-ohm resistors in series carry one ampere with twelve volts across the pair. In parallel across twelve volts, each six-ohm branch carries two amperes. The parallel network draws four amperes in total.',
          },
          'At a junction, incoming current equals outgoing current in steady operation. Summing the branch currents gives I = V/R₁ + V/R₂. Factoring out V shows that the reciprocals of resistance add. The equivalent resistance is less than either individual positive branch resistance, because adding a conducting route allows more total current at the same voltage.',
          equation(
            'Parallel resistance',
            '1/Rₑq',
            '1/R₁ + 1/R₂',
            'This relation adds conductances; invert the sum to obtain resistance.',
          ),
          example(
            'Two unequal branches',
            'A 6-ohm and a 3-ohm resistor are connected in parallel across 12 volts. Their branch currents are 2 and 4 amperes, totaling 6 amperes. The equivalent resistance is 12/6 = 2 ohms. It is not 9 ohms: that would describe a series connection.',
          ),
          'If one branch is opened while the other remains across an ideal voltage source, the surviving branch still sees the same voltage and carries the same current. Total source current falls because one contribution is gone. A real battery with significant internal resistance may behave differently; the ideal-source assumption is part of these questions.',
        ]),
        section('power', 'Energy per charge times charge per time', [
          'Power measures energy transferred per unit time. Multiplying voltage in joules per coulomb by current in coulombs per second gives joules per second, or watts. Thus P = VI. Combining this with the ohmic relation gives P = I²R or P = V²/R. The V and I must refer to the component whose power is being calculated.',
          equation('Electrical power', 'P', 'V × I', 'One watt is one joule per second.'),
          'A 10-volt drop with a 2-ampere current transfers 20 joules each second. Over five seconds at those constant values, the energy is 100 joules. Watts and joules are therefore not interchangeable: one is a rate, the other an amount. Energy supplied by the ideal source equals energy transferred in the resistors when there are no other storage or loss terms.',
          'The forms I²R and V²/R can appear contradictory until the held-fixed quantity is identified. At fixed current, a larger resistance transfers more power. At fixed voltage, a larger resistance transfers less power because it reduces the current. A question about replacing a component cannot be answered from resistance alone without knowing its connections and what the source maintains. This is another reason to start with the circuit model before choosing an equation.',
        ]),
      ],
    },
    questions: [
      choice(
        'b4-circuit-1',
        'What does a current of 2 amperes mean?',
        [
          'Two joules are stored in each coulomb.',
          'Two coulombs pass a cross-section each second.',
          'Two ohms are used each second.',
          'Two volts disappear after every resistor.',
        ],
        1,
        'Two amperes means two coulombs of charge pass each second.',
        ['quantities'],
      ),
      choice(
        'b4-circuit-2',
        'A 5-ohm ohmic resistor has 15 volts across it. What is its current?',
        ['75 A', '10 A', '0.333 A', '3 A'],
        3,
        'Dividing the voltage by the resistance gives I = 15/5 = 3 amperes.',
        ['quantities'],
      ),
      choice(
        'b4-circuit-3',
        'For two resistors in series in a steady circuit, which quantity must be equal through both?',
        ['Current', 'Voltage drop', 'Resistance', 'Power'],
        0,
        'There is only one path, so the same charge rate passes each resistor.',
        ['quantities', 'series'],
      ),
      choice(
        'b4-circuit-4',
        'Resistors of 3 Ω and 9 Ω are in series across an ideal 24 V source. What is the voltage across the 9 Ω resistor?',
        ['6 V', '12 V', '18 V', '24 V'],
        2,
        'The 12-ohm total carries 2 A; the 9-ohm drop is 18 V.',
        ['quantities', 'series'],
      ),
      choice(
        'b4-circuit-5',
        'Resistors of 4 Ω and 12 Ω are in parallel across 12 V. What is the total source current?',
        ['4 A', '1 A', '3 A', '0.75 A'],
        0,
        'The branch currents are 3 A and 1 A, which add to 4 A.',
        ['quantities', 'parallel'],
      ),
      choice(
        'b4-circuit-6',
        'A second positive-resistance branch is added in parallel to an existing resistor across a fixed ideal voltage source. What happens to equivalent resistance?',
        [
          'It increases by the added resistance.',
          'It stays unchanged.',
          'It decreases.',
          'It becomes the arithmetic mean of the branch resistances.',
        ],
        2,
        'An additional conducting path increases total current for the same voltage.',
        ['quantities', 'parallel'],
      ),
      choice(
        'b4-circuit-7',
        'One of two parallel branches is opened. The other remains connected across the same ideal voltage source. What happens to the surviving branch current?',
        [
          'It doubles because all current must choose it.',
          'It falls to zero because any opening breaks the whole circuit.',
          'It becomes equal to the former total current.',
          'It stays the same because its voltage and resistance are unchanged.',
        ],
        3,
        'An ideal fixed-voltage source maintains the voltage on the remaining branch.',
        ['quantities', 'parallel'],
      ),
      choice(
        'b4-circuit-8',
        'A resistor has a 6 V drop and carries 2 A. How much energy does it transfer in 5 seconds at these constant values?',
        ['12 J', '60 J', '30 J', '3 J'],
        1,
        'Power is 12 W; multiplying by five seconds gives 60 J.',
        ['quantities', 'power'],
      ),
      choice(
        'b4-circuit-9',
        'At the same fixed voltage, replacing an ohmic resistor by one with twice the resistance does what to its power?',
        ['Doubles it', 'Quadruples it', 'Halves it', 'Leaves it unchanged'],
        2,
        'P = V²/R, so doubling R at fixed V halves power.',
        ['quantities', 'power'],
      ),
      choice(
        'b4-circuit-10',
        'Why is it misleading to say a resistor “uses up current” in a steady single loop?',
        [
          'Charge flow continues through the resistor while electrical energy is transferred.',
          'No energy can be transferred in a resistor.',
          'Current is a form of stored chemical energy.',
          'The outgoing charge rate is always zero.',
        ],
        0,
        'Charge conservation gives equal current on both sides; energy per charge changes.',
        ['quantities', 'series', 'power'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-circulation',
    category: 'foundations',
    title: 'Blood circulation',
    topic: 'Human pulmonary and systemic circuits',
    difficulty: 'Challenging',
    reading: {
      title: 'Two connected circuits, one continuous flow',
      sources: [
        source(
          'OpenStax Anatomy and Physiology 2e · Cardiac Physiology',
          'https://openstax.org/books/anatomy-and-physiology-2e/pages/19-4-cardiac-physiology',
        ),
        source(
          'OpenStax Anatomy and Physiology 2e · Structure and Function of Blood Vessels',
          'https://openstax.org/books/anatomy-and-physiology-2e/pages/20-1-structure-and-function-of-blood-vessels',
        ),
      ],
      sections: [
        section('route', 'Name vessels by direction, not by oxygen', [
          'The human cardiovascular system moves blood through a closed network of vessels, driven by the heart. An artery carries blood away from the heart; a vein carries blood toward it. These are definitions based on direction. Oxygen content often correlates with those names in the body-wide circuit, but it is not what defines them. In the lung circuit, the association reverses.',
          'The right side of the heart sends blood to the lungs, where gas exchange increases its oxygen content and reduces its carbon-dioxide content. Blood returns to the left side of the heart, which sends it through the body’s tissues. It then returns to the right side. The two sides therefore serve successive parts of a continuous route, not two unrelated supplies of blood.',
          table(
            'Trace one complete route',
            ['Stage', 'Next destination', 'Circuit role'],
            [
              ['Right ventricle', 'Pulmonary arteries and lungs', 'Send blood for gas exchange'],
              [
                'Lung capillaries',
                'Pulmonary veins and left atrium',
                'Return blood after lung exchange',
              ],
              ['Left ventricle', 'Aorta and body arteries', 'Supply body tissues'],
              [
                'Body capillaries',
                'Body veins and right atrium',
                'Return toward the lungs via the heart',
              ],
            ],
          ),
          'An atrium receives blood returning to the heart; a ventricle pumps blood out into an arterial circuit. Between an atrium and its ventricle, and at the ventricular outlets, valves support one-way flow. Right and left refer to the person’s own sides, not the viewer’s sides in a front-facing drawing. Diagrams often color oxygen-rich blood red and oxygen-poor blood blue, but actual human blood is red in both states, with different shades.',
        ]),
        section('exchange', 'Branching brings the exchange surface near cells', [
          'Large arteries branch into smaller arteries and arterioles, which lead to capillary networks. Capillaries have very thin walls, providing short distances for exchange between blood and surrounding tissue fluid. Venules collect blood from these networks and merge into veins. A thick-walled major artery is well suited to carrying flow under pressure but is not the primary surface across which nearby tissue cells exchange their materials.',
          'Oxygen moves from lung air into blood where the relevant gas-pressure difference favors that direction. In body tissues using oxygen, the local difference favors movement from blood toward the tissues. Carbon dioxide produced by metabolism travels in the opposite overall direction, from tissues to blood and ultimately from blood into the lung air for exhalation. The lungs exchange gases; they do not manufacture the oxygen supplied to the blood.',
          example(
            'Follow a change in one circuit',
            'Blood entering a body capillary network has more oxygen than the blood leaving that network when the tissue is extracting oxygen. Yet both streams contain some oxygen. “Oxygen-poor” is a relative description and should not be silently replaced by “contains none.” In the lungs, the relative change is reversed.',
          ),
          'Exchange involves many substances besides oxygen. Nutrients move toward cells that use them, and metabolic products move away, depending on their gradients and transport mechanisms. Blood is the delivery medium, while individual cells perform their own metabolism. A diagram showing blood arriving at an organ is therefore not showing oxygen being converted into blood or nutrients being created by the vessel wall.',
        ]),
        section('pressure', 'Structure reflects pressure and function', [
          'Pressure differences drive bulk blood flow through the vascular network. The heart’s contractions generate high pressure in the arteries leaving it. Pressure falls as flow encounters resistance through the circulation. Arteries therefore have comparatively thick, elastic and muscular walls; they must tolerate and help regulate that flow. Many veins operate at lower pressures and contain valves, especially in limbs.',
          'Venous valves prevent backward flow when pressure changes would otherwise encourage it. They do not create an independent energy source. Contraction of surrounding skeletal muscles can squeeze veins; valves help direct the displaced blood toward the heart. Relaxation then allows the segment to refill. The distinction is between generating a pressure change and directing the resulting flow.',
          example(
            'A squeeze with a direction',
            'Imagine a vein segment between two working one-way valves. When a nearby muscle compresses the segment, the valve toward the heart can open while the valve behind closes. Blood moves forward. Without directional valves, the same squeeze could displace blood both ways, depending on surrounding pressures.',
          ),
          'The left ventricle pumps through the much larger systemic route and normally produces higher pressure than the right ventricle, which pumps through the lungs. This does not mean the left side can continuously pump a greater volume per minute than the right. Their long-term flows must match in a stable closed circulation, or blood would accumulate between them. Pressure and volume per time are different quantities.',
        ]),
        section('integration', 'Keep the route, the contents and the rate separate', [
          'Cardiac output is the volume a ventricle pumps per minute. In a simple calculation it equals heart rate in beats per minute multiplied by stroke volume in volume per beat. The units explain the product: “beats” cancel, leaving volume per minute. This is a rate of movement of blood, not the total amount of blood in the body.',
          equation(
            'A flow-rate calculation',
            'cardiac output',
            'heart rate × stroke volume',
            'For example, 60 beats/min × 80 mL/beat = 4800 mL/min, or 4.8 L/min.',
          ),
          'If heart rate rises while stroke volume falls, the product could increase, decrease or stay the same. Looking at only one factor is insufficient. Likewise, a blood vessel can carry oxygen-poor blood and still be an artery if its direction is away from the heart. Keeping definitions separate prevents a familiar association from being mistaken for a universal rule.',
          'This chapter describes ordinary adult human circulation. Fetal circulation has temporary shunts, and some medical conditions alter the route. The questions specify the ordinary adult model rather than treating one simplified diagram as every possible human condition. Within that model, tracing a small parcel of blood through each chamber and vessel is more reliable than memorizing isolated red and blue labels.',
        ]),
      ],
    },
    questions: [
      choice(
        'b4-blood-1',
        'In ordinary adult circulation, what makes a vessel an artery?',
        [
          'It contains oxygen-rich blood.',
          'It has valves throughout its length.',
          'It always carries blood toward the lungs.',
          'It carries blood away from the heart.',
        ],
        3,
        'Direction away from the heart defines an artery; oxygen content does not.',
        ['route'],
      ),
      choice(
        'b4-blood-2',
        'Which route correctly follows blood leaving the right ventricle?',
        [
          'Aorta → body → left atrium',
          'Pulmonary arteries → lungs → pulmonary veins → left atrium',
          'Pulmonary veins → lungs → aorta → right atrium',
          'Body veins → body capillaries → left ventricle',
        ],
        1,
        'The right ventricle feeds the pulmonary circuit, returning to the left atrium.',
        ['route'],
      ),
      choice(
        'b4-blood-3',
        'Which vessel normally carries relatively oxygen-rich blood toward the heart?',
        ['Pulmonary vein', 'Pulmonary artery', 'Vena cava', 'Aorta'],
        0,
        'Pulmonary veins return blood after lung gas exchange.',
        ['route', 'exchange'],
      ),
      choice(
        'b4-blood-4',
        'Why are capillaries important exchange sites?',
        [
          'Their walls are thick enough to prevent diffusion.',
          'They pump blood independently of the heart.',
          'Their thin walls provide short distances for exchange.',
          'They convert carbon dioxide directly into oxygen.',
        ],
        2,
        'Thin capillary walls and distributed networks support exchange with tissues.',
        ['exchange'],
      ),
      choice(
        'b4-blood-5',
        'Which statement about body tissues using oxygen is most accurate?',
        [
          'Blood normally leaves their capillaries with exactly zero oxygen.',
          'Oxygen-rich blood turns blue after delivering oxygen.',
          'Blood normally loses some oxygen across their capillary networks.',
          'Their veins must be reclassified as arteries when oxygen falls.',
        ],
        2,
        'Oxygen-poor is relative, not an assertion of no remaining oxygen.',
        ['route', 'exchange'],
      ),
      choice(
        'b4-blood-6',
        'What is the main role of one-way valves in limb veins?',
        [
          'Limit backward flow as surrounding pressures change',
          'Generate oxygen from the blood',
          'Create all of the pressure that moves blood',
          'Increase the total number of blood cells',
        ],
        0,
        'Valves direct flow; surrounding muscles and pressure differences supply driving forces.',
        ['pressure'],
      ),
      choice(
        'b4-blood-7',
        'Why can the left ventricle generate higher pressure without continuously pumping more volume per minute than the right?',
        [
          'The right ventricle receives a separate supply that never returns.',
          'Pressure and flow rate differ; stable closed circulation requires matching long-term flows.',
          'Blood disappears after crossing the lungs.',
          'Higher pressure necessarily means lower oxygen content.',
        ],
        1,
        'Different circuit resistances require different pressures, while long-term circuit flows must balance.',
        ['route', 'pressure'],
      ),
      choice(
        'b4-blood-8',
        'A simplified model has heart rate 75 beats/min and stroke volume 80 mL/beat. What is cardiac output?',
        ['0.94 L/min', '60 L/min', '155 mL/min', '6 L/min'],
        3,
        'Multiplying beats per minute by milliliters per beat gives 6000 mL/min, or 6 L/min.',
        ['integration'],
      ),
      choice(
        'b4-blood-9',
        'Heart rate rises from 60 to 80 beats/min while stroke volume falls from 80 to 60 mL/beat. What happens to modeled cardiac output?',
        [
          'It stays at 4.8 L/min.',
          'It rises by one third.',
          'It falls by one quarter.',
          'It doubles.',
        ],
        0,
        'Both products equal 4800 mL per minute.',
        ['integration'],
      ),
      choice(
        'b4-blood-10',
        'What is the ordinary role of the lungs in oxygenating blood?',
        [
          'Manufacture oxygen molecules from carbon dioxide',
          'Pump the systemic circuit instead of the left ventricle',
          'Provide a surface for oxygen to move from inhaled air into blood',
          'Add new blood to replace blood used by tissues',
        ],
        2,
        'The lungs exchange gases; oxygen originates in the inhaled air.',
        ['route', 'exchange'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-reformation',
    category: 'foundations',
    title: 'Printing and religious reform',
    topic: 'Western and central Europe in the early sixteenth century',
    difficulty: 'Challenging',
    reading: {
      title: 'Ideas spread through institutions as well as print',
      sources: [
        source(
          'Catholic Catechism · Indulgences (doctrinal terminology)',
          'https://www.vatican.va/archive/ENG0015/__P4G.HTM',
        ),
        source(
          'OpenStax World History, Volume 2 · The Protestant Reformation',
          'https://openstax.org/books/world-history-volume-2/pages/5-1-the-protestant-reformation',
        ),
      ],
      sections: [
        section('context', 'Distinguish religious authority from political geography', [
          'In early sixteenth-century western and central Europe, the Roman Catholic Church was a powerful institution whose authority crossed political boundaries. Europe was not a single country, however, and rulers could have interests that conflicted with church authorities. Religious practice also was not uniform in every person or community. Jewish communities, earlier dissenting traditions and Europe’s wider religious diversity make a claim that everyone held identical beliefs misleading.',
          'The Protestant Reformation names a set of movements that challenged aspects of Catholic teaching and authority and produced enduring divisions in western Christianity. It was not one reform with one program adopted everywhere at once. Disputes about salvation, scripture and church practices interacted with local rulers, urban institutions and existing efforts at reform.',
          'Christian humanists such as Erasmus sought renewal through learning, textual study and attention to Christian conduct. Humanism in this historical setting emphasized study of language and classical texts; it did not simply mean modern secular unbelief. Criticizing clerical behavior or calling for better education did not necessarily imply wanting to leave the Catholic Church.',
          example(
            'Different kinds of criticism',
            'One critic might condemn an officeholder’s misconduct while accepting the office’s authority. Another might challenge the institution’s authority to decide doctrine. Both are criticisms, but they propose different kinds of change. Reading a historical complaint requires identifying its target rather than assuming every reformer wanted the same institutional break.',
          ),
        ]),
        section('luther', 'From a dispute about practice to a dispute about authority', [
          'Martin Luther’s 1517 Ninety-five Theses challenged the promotion of indulgences, among other related concerns. In Catholic teaching, an indulgence concerned the remission of temporal punishment associated with sins already forgiven; it was not simply an official license to commit future sins. Fundraising practices and popular claims surrounding indulgences could blur these distinctions and became targets of criticism.',
          'Luther objected to the suggestion that a payment could substitute for repentance or secure spiritual benefits in the way sellers implied. The theses initially sought disputation and reform rather than announcing a fully developed new denomination. His later conflict with church authorities widened into disagreement about the basis of religious authority and the means of salvation.',
          'In the Lutheran position that developed, justification by faith and the authority of scripture became central. Describing this contrast requires care: Catholic teaching was not simply that people could buy salvation, and Protestant groups did not agree about every doctrine. A good comparison specifies the claim being discussed instead of assigning one side a caricature.',
          table(
            'Separate related questions',
            ['Question', 'What it concerns'],
            [
              ['Were particular fundraising practices abusive?', 'Conduct and administration'],
              ['Who may authoritatively interpret doctrine?', 'Religious authority'],
              ['What is the role of faith in justification?', 'Theology of salvation'],
              [
                'Who controls church institutions in a territory?',
                'Political and institutional power',
              ],
            ],
          ),
          'These disputes overlapped but were not interchangeable. A ruler could support an institutional break partly for territorial reasons while a theologian defended it with arguments about scripture. Recognizing multiple motives does not require deciding that religious beliefs were insincere or that political interests were irrelevant.',
        ]),
        section('print', 'A medium changes circulation without determining belief', [
          'Printing with movable type made it possible to reproduce texts in larger quantities than hand copying each complete book. Pamphlets and translated texts could move arguments across cities and territories. Luther’s writings reached readers beyond the original university setting, and controversy generated further responses, rebuttals and new editions.',
          'The causal claim is about a mechanism of circulation: more copies could reach more places more quickly and cheaply. It is not that the machine itself made an argument true or forced every reader to accept it. Literacy, language, distribution networks, censorship, patronage and oral discussion affected who encountered a text and how it was interpreted.',
          example(
            'Follow the chain of influence',
            'A printer produces a short pamphlet in a language local readers understand. Merchants carry copies to another town. A reader discusses it with neighbors, including people who cannot read it themselves. That chain explains how print can matter beyond individual silent reading, while still depending on social connections and interpretation.',
          ),
          'Catholic writers and authorities could also use print. A technology that amplifies disagreement is not the exclusive possession of one side. Likewise, printing did not appear globally for the first time with Luther or with European presses: earlier printing traditions existed in East Asia. The relevant historical question here is how European print networks interacted with the sixteenth-century reform movements.',
        ]),
        section('variation', 'Different political settings produced different settlements', [
          'The Holy Roman Empire contained many territories rather than functioning as one centralized nation-state. Some rulers supported Lutheran reform, while the emperor and other rulers opposed it. The Peace of Augsburg in 1555 recognized Catholic and Lutheran territorial choices for rulers within the empire. It did not grant unrestricted individual religious freedom or recognize every Protestant movement on equal terms.',
          'In England, Henry VIII’s break with papal authority was closely connected to a dynastic and marital dispute and to royal control of the English church. A change in who headed the church did not instantly replace every doctrine and ritual with Luther’s program. Religious policy changed further under subsequent monarchs. The English sequence therefore cannot be treated as a simple copy of events in the German territories.',
          'Other reforming groups also differed. Anabaptists rejected infant baptism in favor of a conscious profession associated with adult baptism, distinguishing them from Catholics and many other Protestants. Calvinist traditions developed their own theological emphases and institutions. The label Protestant describes a family of movements, not complete agreement among all its members.',
          'To explain the period, combine ideas, communication and institutions. Doctrine helps explain what people argued over; print helps explain how arguments traveled; political arrangements help explain why reform took root differently across regions. None of these alone supplies a complete cause. The ten questions revisit those distinctions within a bounded early-Reformation unit, rather than attempting to summarize every European religious conflict.',
        ]),
      ],
    },
    questions: [
      choice(
        'b4-reform-1',
        'What is misleading about describing early sixteenth-century western Europe as one politically unified Catholic state?',
        [
          'Shared church authority coexisted with many distinct political jurisdictions.',
          'Every territory shared the same secular ruler.',
          'The pope directly appointed every territorial ruler.',
          'Shared Catholic affiliation guaranteed identical local laws.',
        ],
        0,
        'A transnational church did not turn the region into one centralized state.',
        ['context'],
      ),
      choice(
        'b4-reform-2',
        'A Christian humanist criticizes clerical misconduct while seeking renewal within the church. What follows?',
        [
          'Humanist textual criticism necessarily rejected Christian revelation.',
          'Reforming clerical conduct required accepting Luther’s later theology.',
          'Criticism does not by itself imply rejection of Catholic identity or authority.',
          'Studying classical authors meant abandoning study of Christian sources.',
        ],
        2,
        'Christian humanist reform could occur within Catholic commitments.',
        ['context'],
      ),
      choice(
        'b4-reform-3',
        'Which distinction matters when discussing indulgences?',
        [
          'They were simply official permission to commit future crimes.',
          'Official teaching about temporal punishment differed from a simple claim that salvation could be purchased.',
          'They concerned only taxation and had no religious meaning.',
          'They guaranteed that any payer could ignore repentance.',
        ],
        1,
        'Avoid equating the doctrine or all Catholic belief with sellers’ disputed fundraising claims.',
        ['luther'],
      ),
      choice(
        'b4-reform-4',
        'Which best describes the role of the Ninety-five Theses in the early dispute?',
        [
          'They already settled the doctrines of all later reform movements.',
          'They were written after Lutheran territorial rights had been recognized at Augsburg.',
          'They were issued by Henry VIII to settle the English succession.',
          'They challenged indulgence-related practices before the full later institutional break developed.',
        ],
        3,
        'The 1517 intervention preceded the subsequent widening dispute and denominational developments.',
        ['luther'],
      ),
      choice(
        'b4-reform-5',
        'How did printing help reform arguments spread?',
        [
          'It made all readers agree with the printed conclusion.',
          'It enabled wider reproduction and circulation through existing social networks.',
          'It removed the need for translation or distribution.',
          'It prevented Catholic writers from publishing replies.',
        ],
        1,
        'Print changed the cost and reach of circulation without determining agreement.',
        ['print'],
      ),
      choice(
        'b4-reform-6',
        'Why is a technological explanation of the Reformation incomplete on its own?',
        [
          'Texts still depended on language, readers, institutions and political responses.',
          'No reformer used printed texts.',
          'Printing automatically determined every ruler’s religious policy.',
          'Oral discussion ceased once pamphlets existed.',
        ],
        0,
        'Communication technology worked through social and political conditions.',
        ['context', 'print'],
      ),
      choice(
        'b4-reform-7',
        'What did the Peace of Augsburg recognize in the scope described here?',
        [
          'An individual right to choose any denomination within every territory',
          'Equal territorial recognition for Lutheran, Calvinist and Anabaptist rulers',
          'Exclusive imperial control of religion regardless of territorial rulers',
          'Catholic or Lutheran territorial choices by rulers within the empire',
        ],
        3,
        'The settlement’s recognized choices were bounded; it was not universal individual freedom.',
        ['variation'],
      ),
      choice(
        'b4-reform-8',
        'Which helps distinguish Henry VIII’s initial break with Rome from a simple adoption of Luther’s whole program?',
        [
          'It immediately adopted the same doctrinal settlement as Lutheran territories.',
          'It began by allowing each English congregation to choose its own head.',
          'It centered on dynastic conflict and royal church authority while many doctrines initially remained.',
          'It immediately removed all practices shared with Catholic worship.',
        ],
        2,
        'Political authority and doctrine are separate dimensions of change.',
        ['luther', 'variation'],
      ),
      choice(
        'b4-reform-9',
        'Why is “all Protestants agreed on baptism” inaccurate for this period?',
        [
          'Lutherans and Anabaptists both required adult rebaptism.',
          'Reform movements agreed that baptism had no religious significance.',
          'Anabaptists rejected infant baptism, unlike many other Protestants.',
          'The label Protestant implied a single common baptismal practice.',
        ],
        2,
        'Protestant movements differed; adult baptism was a defining Anabaptist distinction.',
        ['variation'],
      ),
      choice(
        'b4-reform-10',
        'A ruler supports reform for territorial control while a theologian argues from scripture. Which interpretation is strongest?',
        [
          'The political motive proves all religious arguments were insincere.',
          'Political and religious motives can operate together without being identical.',
          'The religious argument proves political interests were absent.',
          'One shared outcome requires every supporter to have the same reason.',
        ],
        1,
        'Multiple actors can support related changes for partly different reasons.',
        ['context', 'luther', 'variation'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-distributions',
    category: 'foundations',
    title: 'Describing a distribution',
    topic: 'Mean, median, quartiles, spread and transformations',
    difficulty: 'Challenging',
    reading: {
      title: 'A center is only part of the data',
      sources: [
        source(
          'NIST/SEMATECH Handbook · Measures of Location',
          'https://www.itl.nist.gov/div898/handbook/eda/section3/eda351.htm',
        ),
        source(
          'NIST/SEMATECH Handbook · Measures of Scale',
          'https://www.itl.nist.gov/div898/handbook/eda/section3/eda356.htm',
        ),
        source(
          'NIST/SEMATECH Handbook · Box Plot',
          'https://www.itl.nist.gov/div898/handbook/eda/section3/boxplot.htm',
        ),
      ],
      sections: [
        section('center', 'Different summaries preserve different information', [
          'A distribution describes what values occur and how frequently they occur. A measure of center compresses that information into one number, but different centers answer different questions. The arithmetic mean is the sum of all values divided by the number of values. It is the equal-share value: if the total were redistributed evenly, each observation would receive the mean.',
          'The median is the middle ordered value when there is an odd number of observations, or the average of the two middle values when there is an even number. It identifies the center of the ordered positions rather than sharing out the total. Sorting is therefore essential for finding a median, even though sorting does not change a mean.',
          example(
            'One long wait',
            'Waiting times are 2, 3, 3, 4 and 18 minutes. Their mean is 30/5 = 6 minutes, while their median is 3 minutes. The long wait contributes strongly to the total but occupies only one position in the ordering. Neither summary is an arithmetic error; they preserve different features.',
          ),
          'The mode is a most frequent value. A dataset can have more than one equally frequent mode, and a mode need not be near the mean. Choosing a summary should depend on what the reader needs to know. For a typical ranked experience, the median may be informative; for total staffing time across many cases, the sum and mean matter directly.',
        ]),
        section('spread', 'Describe how far values extend around the center', [
          'Two datasets can have the same mean and very different variability. The range is maximum minus minimum; it notices only the endpoints. The interquartile range, or IQR, is the upper quartile minus the lower quartile and describes the width of the middle part of the ordered data. Because it does not directly use the extreme endpoints, it is less sensitive than range to moving one already-extreme observation farther away.',
          'Quartile conventions differ across textbooks and software, particularly for small datasets. The questions here explicitly use the median-of-halves convention: for an even-sized ordered dataset, split it into equal lower and upper halves and take the median of each half. For an odd-sized dataset, exclude the overall median before making the two halves. A numerical disagreement caused solely by another legitimate convention is not evidence that someone has forgotten arithmetic.',
          example(
            'Work an explicit quartile convention',
            'For 1, 2, 4, 5, 7, 8, 10 and 20, the lower half is 1, 2, 4, 5 and its median is 3. The upper half is 7, 8, 10, 20 and its median is 9. Thus Q₁=3, Q₃=9 and IQR=6. The overall median is (5+7)/2=6, while the range is 19.',
            table(
              'Centers and widths',
              ['Summary', 'Value'],
              [
                ['Median', '6'],
                ['Q₁ and Q₃', '3 and 9'],
                ['IQR', '6'],
                ['Range', '19'],
              ],
            ),
          ),
          'An IQR is a distance in the same units as the data, not a percentage of observations. Calling it six minutes says how wide an interval is. The phrase middle half refers to ranked positions, with interpolation and ties affecting exact counts in small datasets.',
        ]),
        section('shape', 'Interpret an extreme value without automatically deleting it', [
          'A long right tail contains a few unusually large values relative to the main cluster. Such values often pull the mean above the median. This is a common pattern, not a theorem that determines every dataset from one inequality between its summaries. A histogram or an ordered list contains information that a pair of center numbers cannot fully recover.',
          'A common screening rule marks values below Q₁−1.5×IQR or above Q₃+1.5×IQR as potential outliers. A fence is a diagnostic threshold, not proof that a value is incorrect. An unusual measurement might be a recording error, a rare valid event, or evidence of a different process. The next step is investigation, not automatic deletion to make the data look tidy.',
          equation(
            'Upper screening fence',
            'upper fence',
            'Q₃ + 1.5 × IQR',
            'Values strictly above it are flagged under this convention.',
          ),
          example(
            'A flag is a question',
            'In the previous eight-value example, the upper fence is 9 + 1.5×6 = 18. The value 20 is flagged. If these are genuine waiting times, removing the longest wait would hide part of the service experience. If 20 was entered instead of 2, correcting it would be appropriate—but that conclusion needs evidence beyond the flag.',
          ),
          'Comparisons also need the same measurement definitions and populations. A lower median wait does not guarantee a lower maximum or less variability. A group can improve its typical case while retaining a few severe delays, so reporting center and spread together helps distinguish these outcomes.',
        ]),
        section('transform', 'Changing units changes some summaries but not others', [
          'Adding the same constant c to every value shifts the whole distribution without changing the gaps between observations. Mean, median and quartiles all rise by c. Range and IQR stay unchanged because the added constants cancel when subtracting endpoints. This distinguishes a change in baseline from a change in variability.',
          'Multiplying every value by a positive factor a stretches every gap by that factor. Mean, median, quartiles, range and IQR all multiply by a. For a negative factor, the order reverses; spread measures still multiply by the factor’s absolute value. Questions in this unit use positive changes of units unless they explicitly say otherwise.',
          example(
            'Convert a scale',
            'If temperature values in Celsius have mean 20 and IQR 8, converting each by F = 1.8C + 32 gives mean 68°F and IQR 14.4°F. The +32 shifts both quartile endpoints equally and cancels from their difference. Adding 32 to the IQR would incorrectly treat a spread as a position.',
          ),
          'Finally, a combined mean needs group-size weights. A group of two observations at mean 10 contributes total 20; a group of eight at mean 20 contributes total 160. Together the mean is 180/10 = 18, not the unweighted average 15. Reconstructing totals is often clearer than memorizing a weighted-mean formula. Neither subgroup medians nor IQRs can generally be pooled by the same simple averaging rule, because their definitions depend on the full ordering.',
        ]),
      ],
    },
    questions: [
      choice(
        'b4-distribution-1',
        'For the values 1, 2, 2, 3 and 17, which pair gives mean and median?',
        ['Mean 2, median 5', 'Mean 5, median 2', 'Mean 5, median 3', 'Mean 25, median 2'],
        1,
        'The sum is 25 over five values; the middle ordered value is 2.',
        ['center'],
      ),
      choice(
        'b4-distribution-2',
        'One already-largest value increases substantially while all other values stay fixed in a five-value dataset. Which summary must increase?',
        ['Mean', 'Median', 'Mode', 'Lower quartile'],
        0,
        'The total increases with the number of observations unchanged.',
        ['center'],
      ),
      choice(
        'b4-distribution-3',
        'Using the median-of-halves convention, what is the IQR of 2, 4, 6, 8, 10, 12, 14, 16?',
        ['7', '14', '8', '9'],
        2,
        'Q₁=(4+6)/2=5 and Q₃=(12+14)/2=13, so IQR=8.',
        ['center', 'spread'],
      ),
      choice(
        'b4-distribution-4',
        'A dataset has Q₁=10 and Q₃=18. Under the 1.5×IQR upper-fence rule, which value is flagged as high?',
        ['26', '30', '28', '31'],
        3,
        'The upper fence is 18+1.5×8=30; only 31 is strictly above it.',
        ['center', 'spread', 'shape'],
      ),
      choice(
        'b4-distribution-5',
        'A valid observation lies above an outlier fence. What does the flag justify by itself?',
        [
          'Investigating its context and measurement',
          'Replacing it with the mean',
          'Concluding it was entered incorrectly',
          'Discarding it from every analysis',
        ],
        0,
        'An outlier rule identifies unusual values, not automatic errors.',
        ['center', 'spread', 'shape'],
      ),
      choice(
        'b4-distribution-6',
        'Add 7 to every observation. What happens to the IQR?',
        ['It rises by 7.', 'It is multiplied by 7.', 'It stays unchanged.', 'It falls by 7.'],
        2,
        'Both quartiles shift by seven, so their difference is unchanged.',
        ['spread', 'transform'],
      ),
      choice(
        'b4-distribution-7',
        'A dataset has IQR 5. Each value x is replaced with 3x+10. What is the new IQR?',
        ['25', '15', '5', '10'],
        1,
        'Positive scaling triples spread; adding ten does not change it.',
        ['center', 'spread', 'transform'],
      ),
      choice(
        'b4-distribution-8',
        'A group of 3 observations has mean 4; another group of 9 has mean 12. What is the combined mean?',
        ['8', '9', '16', '10'],
        3,
        'The total is 3×4+9×12=120 over twelve observations, giving 10.',
        ['center', 'transform'],
      ),
      choice(
        'b4-distribution-9',
        'Two datasets have the same mean. Which conclusion is warranted?',
        [
          'They must have the same range.',
          'They must have the same median.',
          'Their equal-share values match, but their variability may differ.',
          'Their ordered lists must be identical.',
        ],
        2,
        'A mean does not determine the distribution or its spread.',
        ['center', 'spread'],
      ),
      choice(
        'b4-distribution-10',
        'Why should a small-data quartile calculation state its convention?',
        [
          'Different legitimate interpolation or splitting conventions can yield different quartiles.',
          'Quartiles have no mathematical definitions.',
          'The mean changes whenever data are sorted.',
          'Every software package uses exactly the same quartile rule.',
        ],
        0,
        'Several conventions are used; specifying one makes the requested result unambiguous.',
        ['center', 'spread'],
      ),
    ],
  }),
];
